// Minimal shape of the Sentry event fields these filters inspect.
// Compatible with ErrorEvent from @sentry/react through structural typing.
interface InstrumentEvent {
  exception?: {
    values?: Array<{
      type?: string;
      value?: string;
      mechanism?: { type?: string; handled?: boolean };
      stacktrace?: {
        frames?: Array<{ in_app?: boolean; function?: string; filename?: string }>;
      };
    }>;
  };
}

// Global symbols injected by third-party marketing/tracking scripts (LinkedIn
// Insight Tag, Google/Bing ads, Facebook Pixel, ...). When one is invoked —
// usually through Segment or Google Tag Manager — before the vendor script has
// loaded (blocked by an ad-blocker, pending cookie consent, or a slow network),
// the browser throws "window.<symbol> is not a function". These surface via the
// global onerror handler with no Qonto code in the stack, so they are pure
// monitoring noise.
const THIRD_PARTY_TRACKING_SYMBOLS = [
  'lintrk', // LinkedIn Insight Tag
  'uetq', // Bing / Microsoft Ads UET
  'fbq', // Facebook Pixel
  'gtag', // Google Ads / GA4
  'obApi', // Outbrain
  'pintrk', // Pinterest Tag
  'ttq', // TikTok Pixel
  'snaptr', // Snapchat Pixel
  'twq', // Twitter / X Pixel
];

// Matches "window.<symbol> is not a function" and the "<symbol> is not a
// function" variant, for any known tracking symbol.
const THIRD_PARTY_TRACKING_MESSAGE = new RegExp(
  `\\b(?:window\\.)?(?:${THIRD_PARTY_TRACKING_SYMBOLS.join('|')}) is not a function\\b`
);

// Returns true when every exception in the event was captured by Sentry's
// setTimeout/setInterval instrumentation wrapper (mechanism.type=instrument)
// and the only in-app stack frame is the SDK's own sentryWrapped helper.
// This pattern exclusively matches third-party tracking script errors
// (LinkedIn, Google, Bing ads) — no Qonto code is ever in the stack.
// Structural filtering is used here rather than message-string matching so
// the filter is locale-independent (REGISTER-JS-8C2).
//
// Usage: in the register React app's beforeSend:
//   if (isThirdPartyInstrumentError(event)) return null;
export function isThirdPartyInstrumentError(event: InstrumentEvent): boolean {
  const values = event.exception?.values ?? [];
  if (values.length === 0) return false;

  return values.every(exception => {
    if (exception.mechanism?.type !== 'instrument') return false;
    if (exception.mechanism.handled !== false) return false;

    const frames = exception.stacktrace?.frames ?? [];
    if (frames.length === 0) return false;

    // Every in-app frame must be the SDK's own sentryWrapped timer wrapper.
    // Frames already marked in_app: false are third-party by definition.
    return frames.every(frame => !frame.in_app || frame.function === 'sentryWrapped');
  });
}
// Returns true when every exception is an unhandled "window.<tracking-symbol>
// is not a function" TypeError. This matches third-party tracking scripts
// (LinkedIn `lintrk`, Bing `uetq`, ...) invoked through Segment / Google Tag
// Manager before the vendor script has loaded. Unlike
// isThirdPartyInstrumentError (mechanism=instrument), this covers the
// onerror-captured variant. See REGISTER-PARTNERS-JS-3E4.
//
// Frames are deliberately not inspected: the browser SDK marks every frame
// in_app, and GTM/Segment bundles are served from our own origin, so neither
// in_app nor filename tells vendor code apart from ours. The message alone is
// enough because no Qonto code calls these tracker globals (Sentry 7750652001).
export function isThirdPartyTrackingScriptError(event: InstrumentEvent): boolean {
  const values = event.exception?.values ?? [];
  if (values.length === 0) return false;

  return values.every(exception => {
    if (exception.mechanism?.handled !== false) return false;
    return Boolean(exception.value && THIRD_PARTY_TRACKING_MESSAGE.test(exception.value));
  });
}

// In-app browsers (Facebook/Instagram on iOS) inject scripts that call the
// native `window.webkit.messageHandlers.<name>.postMessage`, which returns a
// promise when the host registers a reply handler. If the host rejects it,
// WebKit raises an unhandled InvalidAccessError whose top frame is the native
// postMessage. Register code never calls a native postMessage, so this is
// injected-script noise (Sentry issue 7771588519).
// Matched on stack shape, not the message, because the WebKit message is
// generic to every InvalidAccessError.
export function isInAppBrowserBridgeRejection(event: InstrumentEvent): boolean {
  const values = event.exception?.values ?? [];
  if (values.length === 0) return false;

  return values.every(exception => {
    if (exception.type !== 'InvalidAccessError') return false;
    if (exception.mechanism?.handled !== false) return false;
    if (exception.mechanism.type !== 'auto.browser.global_handlers.onunhandledrejection') {
      return false;
    }

    // Sentry orders frames oldest-first, so the last one is where it threw.
    const topFrame = exception.stacktrace?.frames?.at(-1);
    return topFrame?.function === 'postMessage' && topFrame.filename === '[native code]';
  });
}
